import { cn } from '@/lib/utils';

import { BadgeProps } from '@/decodo/components/atoms/badge';
import NextImage, { NextImageProps } from '@/decodo/components/atoms/Image';
import LinkButton from '@/decodo/components/atoms/LinkButton';
import { Text } from '@/decodo/components/atoms/Text';

import { MenuItemPricing } from './MenuItemPricing';

import IconChevronRight from '~/icons/chevron-right.svg';

export type MenuItemProps = {
  icon?: NextImageProps;
  title: string;
  badge?: React.ReactNode;
  url: string;
  showChevron?: boolean;
  description?: string;
  price?: string;
  priceUnit?: string;
  priceLabel?: string;
  onLinkClick?: (
    params: { title: string } | { url: string; keepMenuOpen?: boolean }
  ) => void;
};

export function MenuItem({
  icon,
  title,
  description,
  badge: Badge,
  price,
  priceUnit,
  priceLabel = 'Starts from',
  url,
  showChevron,
  onLinkClick,
}: MenuItemProps) {
  const isPricingItem = !!price;

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    title: string,
    url: string
  ) => {
    const currentPath = window.location.pathname;

    if (url === currentPath) {
      e.preventDefault();
      onLinkClick?.({ title, url });
      return;
    }

    onLinkClick?.({ title, url });
  };

  return (
    <div
      className={cn(
        'group/menu-item relative grid cursor-pointer auto-cols-[32px_1fr_auto] grid-flow-col items-center gap-x-3 gap-y-1 p-2',
        !isPricingItem && 'headerLg:items-start'
      )}
    >
      <div className='hover-hover:group-hover/menu-item:bg-brand-100 group-focus-within/menu-item:bg-brand-100 absolute inset-0 rounded transition-colors duration-200'></div>
      {!!icon && (
        <NextImage
          {...icon}
          classNames={{
            container: 'h-8 w-8 shrink-0 select-none col-start-1',
            image: 'h-full w-full rounded',
          }}
          aria-hidden
        />
      )}
      <div
        className={cn(
          'col-start-2 flex items-center gap-x-2 overflow-hidden',
          !icon &&
            'group-data-[type=suffix]/menu-list-item:headerLg:col-start-1 col-span-2 col-start-1',
          !isPricingItem && 'headerLg:self-start col-span-2'
        )}
      >
        <LinkButton
          variant='text'
          noStyles
          href={url}
          // eslint-disable-next-line
          // @ts-ignore
          onClick={handleClick}
          className='z-[1] overflow-hidden outline-none before:absolute before:inset-0 before:isolate before:cursor-pointer'
        >
          <Text
            variant='h5'
            classNames={{
              wrapper: 'truncate font-serif select-text text-base-dark-black',
            }}
          >
            {title}
          </Text>
        </LinkButton>
        {Badge}
        {showChevron && (
          <div className='relative flex h-6 w-6 shrink-0 items-center justify-center'>
            <IconChevronRight className='text-base-dark-black headerLg:h-full headerLg:w-full h-5 w-5' />
          </div>
        )}
      </div>
      {!!description && (
        <Text
          variant='body-m'
          color='gray-dark-500'
          classNames={{
            wrapper:
              'row-start-2 col-start-1 col-span-2 relative  min-w-[250px] max-w-[400px]',
          }}
        >
          {description}
        </Text>
      )}
      {!!price && (
        <MenuItemPricing
          price={price}
          priceUnit={priceUnit}
          priceLabel={priceLabel}
        />
      )}
    </div>
  );
}

export const badgeInnerProps = {
  variant: 'outlined',
  classNames: {
    root: 'uppercase select-none text-base-dark-black',
  },
} satisfies BadgeProps;
